import * as React from 'react'
import { parseAsString, useQueryState } from 'nuqs'

import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { IDEAS_UPLOADER_ID } from './constants'
import { isExistingIdea } from './helpers'
import type { DraftIdea } from './IdeaComposer'
import { useCreateIdea } from './useCreateIdea'
import type { LegacyCreateIdeaMutation } from './useCreateIdea'
import { useEditIdea } from './useEditIdea'
import type { LegacyUpdateIdeaMutation } from './useEditIdea'
import { useOrganizationId } from '../../contexts/AccountContext'
import { isCta } from '../../helpers/isCta'
import { useAppSelector } from '../../legacy/store'
import { selectIncludedMedia } from '../../legacy/uploads/state/selectors'
import { logError } from '../../legacy/utils/logError'

export type SaveIdeaError = {
  message: string
  code?:
    | 'InvalidInputError'
    | 'UnauthorizedError'
    | 'UnexpectedError'
    | 'LimitReachedError'
    | 'unknown'
}

const DEFAULT_ERROR_MESSAGE =
  'We ran into an error saving your idea. Please try again.'

const isCreateSuccess = (
  result: LegacyCreateIdeaMutation['createIdea'],
): result is Extract<
  LegacyCreateIdeaMutation['createIdea'],
  { __typename: 'Idea' | 'IdeaResponse' }
> => {
  return (
    result['__typename'] === 'Idea' || result['__typename'] === 'IdeaResponse'
  )
}

const isUpdateSuccess = (
  result: LegacyUpdateIdeaMutation['updateIdea'],
): result is Extract<
  LegacyUpdateIdeaMutation['updateIdea'],
  { __typename: 'IdeaResponse' }
> => {
  return result['__typename'] === 'IdeaResponse'
}

type UseSaveIdeaProps = {
  onCompleted?: () => void
  onError?: (error: ErrorLike) => void
}

type UseSaveIdeaReturn = {
  saveIdea: (idea: DraftIdea) => void
  isLoading: boolean
  error: SaveIdeaError | null
}

/**
 * Convenience wrapper hook to save (create or update) an idea.
 * It wraps the two mutations to handle some of the shared complete/error logic.
 * TODO: This wrapper could probably be removed if we simplify error handling.
 */
export const useSaveIdea = ({
  onCompleted,
  onError,
}: UseSaveIdeaProps): UseSaveIdeaReturn => {
  const organizationId = useOrganizationId()
  const [error, setError] = React.useState<SaveIdeaError | null>(null)
  const [placeAfterIdParam] = useQueryState('placeAfterId', parseAsString)
  const placeAfterId = placeAfterIdParam ?? undefined
  const [ideaComposerSourceParam] = useQueryState(
    'ideaComposerSource',
    parseAsString,
  )
  const ideaComposerSource =
    typeof ideaComposerSourceParam === 'string' &&
    isCta(ideaComposerSourceParam)
      ? ideaComposerSourceParam
      : undefined
  const [aiAssistedParam] = useQueryState('aiAssisted', parseAsString)
  const aiAssisted = aiAssistedParam === 'true'

  const media = useAppSelector((state) =>
    selectIncludedMedia(state, IDEAS_UPLOADER_ID),
  )

  const resetErrorMessage = React.useCallback(() => {
    setTimeout(() => {
      setError(null)
    }, 5000)
  }, [])

  const onMutationError = (mutationError: ErrorLike): void => {
    logError(
      mutationError instanceof Error
        ? mutationError
        : new Error(mutationError.message),
      {
        metaData: { organizationId },
      },
    )
    setError({ message: DEFAULT_ERROR_MESSAGE })
    resetErrorMessage()
    onError?.(mutationError)
  }

  // Idea hooks
  const { createIdea, createIdeaLoading } = useCreateIdea({
    onCompleted: (data: LegacyCreateIdeaMutation) => {
      const result = data.createIdea
      if (isCreateSuccess(result)) {
        setError(null)
        onCompleted?.()
        toast.success('Idea created successfully')
      } else {
        setError({
          message: result.message,
          code: result['__typename'] ?? 'unknown',
        })
        resetErrorMessage()
      }
    },
    onError: onMutationError,
  })

  const { updateIdea, updateIdeaLoading } = useEditIdea({
    onCompleted: (data: LegacyUpdateIdeaMutation) => {
      const result = data.updateIdea
      if (isUpdateSuccess(result)) {
        setError(null)
        onCompleted?.()
        toast.success('Idea updated successfully')
      } else {
        setError({
          message: result.message,
          code: result['__typename'] ?? 'unknown',
        })
        resetErrorMessage()
      }
    },
    onError: onMutationError,
  })

  const saveIdea = React.useCallback(
    (idea: DraftIdea) => {
      if (!idea) {
        return
      }
      const { groupId } = idea || {}

      // If the current idea is an existing idea, update it
      // otherwise create a new idea
      if (isExistingIdea(idea)) {
        updateIdea(
          {
            ...idea,
            content: {
              ...idea.content,
              media,
              aiAssisted,
            },
          },
          { source: ideaComposerSource },
        )
      } else {
        createIdea({
          content: { ...idea.content, aiAssisted, media },
          groupId,
          placeAfterId,
          source: ideaComposerSource,
        })
      }
    },
    [
      createIdea,
      ideaComposerSource,
      placeAfterId,
      updateIdea,
      media,
      aiAssisted,
    ],
  )

  return {
    saveIdea,
    isLoading: createIdeaLoading || updateIdeaLoading,
    error,
  }
}
